import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Loader2, Trash2, RefreshCw, X,
  ZoomIn, Plus, ChevronUp, ChevronDown, Repeat, Check, Tag,
} from 'lucide-react';
import { api } from '../../lib/api';
import { PLATFORM_NAMES, Platform } from '@crosova/shared';

const CONDITIONS = [
  { value: 'new_with_tags', label: 'New with tags' },
  { value: 'new_without_tags', label: 'New without tags' },
  { value: 'excellent', label: 'Excellent / Like new' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair / Acceptable' },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040', facebook: '#1877f2',
};

const SYNC_STATUSES = ['live', 'failed', 'publishing'];

export function ListingEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: listing, isLoading } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => api.get(`/listings/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: allPlatforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.get('/platforms').then((r) => r.data),
  });
  const connectedPlatforms: string[] = (allPlatforms as any[]).filter((p) => p.connected).map((p) => p.platform);

  const [form, setForm] = useState({
    title: '', description: '', price: '', buyPrice: '',
    condition: 'good', brand: '', size: '', colour: '', material: '',
    category: '', subcategory: '', sku: '',
    tags: [] as string[],
  });
  const [tagInput, setTagInput] = useState('');
  const [photos, setPhotos] = useState<{ id: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [removingBgId, setRemovingBgId] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [syncingPlatform, setSyncingPlatform] = useState<string | null>(null);
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (listing) {
      setForm({
        title: listing.title ?? '',
        description: listing.description ?? '',
        price: listing.price ? (listing.price / 100).toFixed(2) : '',
        buyPrice: listing.buyPricePence ? (listing.buyPricePence / 100).toFixed(2) : '',
        condition: listing.condition ?? 'good',
        brand: listing.brand ?? '',
        size: listing.size ?? '',
        colour: listing.colour ?? '',
        material: '',
        category: listing.category ?? '',
        subcategory: listing.subcategory ?? '',
        sku: listing.sku ?? '',
        tags: listing.tags ?? [],
      });
      setPhotos(
        [...(listing.photos ?? [])].sort((a: any, b: any) => a.position - b.position)
      );
    }
  }, [listing]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/listings/${id}`, {
        title: form.title,
        description: form.description,
        price: Math.round(parseFloat(form.price || '0') * 100),
        buyPricePence: form.buyPrice ? Math.round(parseFloat(form.buyPrice) * 100) : undefined,
        condition: form.condition,
        brand: form.brand || undefined,
        size: form.size || undefined,
        colour: form.colour || undefined,
        category: form.category || undefined,
        subcategory: form.subcategory || undefined,
        sku: form.sku || undefined,
        tags: form.tags,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const deleteListing = useMutation({
    mutationFn: () => api.delete(`/listings/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['listings'] }); navigate('/dashboard/inventory'); },
  });

  const sync = useMutation({
    mutationFn: (platform: string) =>
      api.post(`/listings/${id}/crosslist`, { platforms: [platform] }).then((r) => r.data),
    onMutate: (platform) => setSyncingPlatform(platform),
    onSettled: () => setSyncingPlatform(null),
    onSuccess: () => navigate('/dashboard/jobs'),
  });

  const crosslist = useMutation({
    mutationFn: (platforms: string[]) =>
      api.post(`/listings/${id}/crosslist`, { platforms }).then((r) => r.data),
    onSuccess: () => { setSelectedPlatforms(new Set()); navigate('/dashboard/jobs'); },
  });

  const deletePhoto = useMutation({
    mutationFn: (photoId: string) => api.delete(`/listings/${id}/photos/${photoId}`),
    onSuccess: (_, photoId) => setPhotos((prev) => prev.filter((p) => p.id !== photoId)),
  });

  const reorderPhotos = useMutation({
    mutationFn: (ordered: { id: string; url: string }[]) =>
      api.post(`/listings/${id}/photos/reorder`, { photoIds: ordered.map((p) => p.id) }),
  });

  // ── Photo actions ─────────────────────────────────────────────────────────

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const remaining = 12 - photos.length;
    if (remaining <= 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await Promise.all(
        files.slice(0, remaining).map(async (file) => {
          const fd = new FormData();
          fd.append('file', file);
          // Do NOT set Content-Type manually — axios sets it with the correct multipart boundary
          const res = await api.post<{ url: string; s3Key: string }>('/uploads/photo', fd);
          const attached = await api.post<{ id: string; url: string }>(`/listings/${id}/photos/attach`, { url: res.data.url, s3Key: res.data.s3Key });
          return attached.data;
        }),
      );
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch {
      setUploadError('Some photos failed to upload. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRemoveBg(photoId: string) {
    setRemovingBgId(photoId);
    try {
      const res = await api.post<{ id: string; url: string }>('/uploads/remove-bg', { photoId });
      setPhotos((prev) => prev.map((p) => p.id === photoId ? { ...p, url: res.data.url } : p));
    } catch {
      /* silently fail — bg removal is best-effort */
    } finally {
      setRemovingBgId(null);
    }
  }

  function movePhoto(from: number, to: number) {
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPhotos(next);
    reorderPhotos.mutate(next);
  }

  // ── Tag actions ───────────────────────────────────────────────────────────

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t) && form.tags.length < 20) {
      setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    }
    setTagInput('');
  }

  function removeTag(tag: string) {
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));
  }

  function field(key: keyof Omit<typeof form, 'tags'>) {
    return {
      value: form[key] as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value })),
    };
  }

  if (isLoading) return <div className="flex items-center justify-center h-48"><Loader2 size={24} className="animate-spin text-white/30" /></div>;
  if (!listing) return <div className="text-center py-16 text-white/30 text-sm">Listing not found.</div>;

  const platformConnections: any[] = listing.platformListings ?? [];

  return (
    <div className="flex flex-col gap-4 max-w-2xl pb-8">

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setLightboxPhoto(null)}>
          <button className="absolute top-4 right-4 text-white/50 hover:text-white" onClick={() => setLightboxPhoto(null)}><X size={24} /></button>
          <img src={lightboxPhoto} alt="" className="max-w-full max-h-full object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Delete listing confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface border border-white/[0.08] rounded-2xl w-full max-w-sm p-5">
            <h2 className="text-sm font-semibold mb-1">Delete this listing?</h2>
            <p className="text-[11px] text-white/40 mb-5">Removes from Crosova. Does not delist from any marketplace.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={() => deleteListing.mutate()} disabled={deleteListing.isPending} className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-danger/80 hover:bg-danger text-white">
                {deleteListing.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white/30 hover:text-white/60 transition-colors"><ArrowLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold tracking-tight truncate">{listing.title}</h1>
          <p className="text-[11px] text-white/25 mt-0.5 capitalize">{listing.status}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-danger/70 hover:text-danger hover:bg-danger/[0.08] border border-danger/20">
            <Trash2 size={13} /> Delete
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className={`btn-primary flex items-center gap-1.5 !text-[12px] ${saved ? '!bg-secondary-500/80' : ''}`}>
            {save.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {save.isPending ? 'Saving...' : saved ? 'Saved ✓' : 'Save'}
          </button>
        </div>
      </div>

      {/* ── Photos ─────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[13px] font-medium">Photos</div>
            <div className="text-[10.5px] text-white/30">{photos.length}/12 · First photo is the cover</div>
          </div>
          <label className={`btn-secondary !text-[11px] !py-1.5 cursor-pointer flex items-center gap-1.5 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {uploading ? 'Uploading...' : 'Add photos'}
            <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading || photos.length >= 12} />
          </label>
        </div>

        {photos.length === 0 ? (
          <label className="block border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-white/20 transition-colors">
            <Plus size={24} className="mx-auto text-white/20 mb-2" />
            <p className="text-sm text-white/30">Click to add photos</p>
            <p className="text-[10.5px] text-white/20 mt-1">JPEG, PNG, WEBP · Up to 12 photos</p>
            <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
          </label>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {photos.map((photo, idx) => (
              <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden bg-white/[0.04]">
                <img src={photo.url} alt="" className="w-full h-full object-cover" />

                {/* Cover badge */}
                {idx === 0 && (
                  <div className="absolute top-1.5 left-1.5 text-[9px] font-semibold bg-primary-500/80 text-white px-1.5 py-0.5 rounded-md">Cover</div>
                )}

                {/* Hover controls */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                  <div className="flex gap-1.5">
                    <button onClick={() => setLightboxPhoto(photo.url)} className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" title="View full size">
                      <ZoomIn size={12} className="text-white" />
                    </button>
                    <button onClick={() => deletePhoto.mutate(photo.id)} className="w-7 h-7 rounded-full bg-danger/30 hover:bg-danger/50 flex items-center justify-center" title="Delete">
                      <Trash2 size={12} className="text-white" />
                    </button>
                    <button
                      onClick={() => handleRemoveBg(photo.id)}
                      disabled={removingBgId === photo.id}
                      className="w-7 h-7 rounded-full bg-secondary-400/20 hover:bg-secondary-400/40 flex items-center justify-center"
                      title="Remove background"
                    >
                      {removingBgId === photo.id ? <Loader2 size={11} className="text-white animate-spin" /> : <span className="text-[9px] font-bold text-white">BG</span>}
                    </button>
                  </div>
                  {/* Reorder */}
                  <div className="flex gap-1">
                    <button onClick={() => movePhoto(idx, idx - 1)} disabled={idx === 0} className="w-6 h-6 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20" title="Move left">
                      <ChevronUp size={11} className="text-white -rotate-90" />
                    </button>
                    <button onClick={() => movePhoto(idx, idx + 1)} disabled={idx === photos.length - 1} className="w-6 h-6 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20" title="Move right">
                      <ChevronDown size={11} className="text-white -rotate-90" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {/* Add more slot */}
            {photos.length < 12 && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-white/10 hover:border-white/25 flex items-center justify-center cursor-pointer transition-colors">
                <Plus size={20} className="text-white/20" />
                <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            )}
          </div>
        )}
        {uploadError && <p className="text-[11px] text-danger mt-2">{uploadError}</p>}
      </div>

      {/* ── Core details ────────────────────────────────────── */}
      <div className="card p-4 flex flex-col gap-4">
        <div className="text-[13px] font-medium border-b border-white/[0.05] pb-2">Listing details</div>

        <Field label={`Title (${form.title.length}/80)`}>
          <input className="input" {...field('title')} maxLength={80} placeholder="Be descriptive — brand, type, size, colour" />
        </Field>

        <Field label="Description">
          <textarea className="input min-h-[140px] resize-y leading-relaxed" {...field('description')} maxLength={5000} placeholder="Describe condition, any flaws, included items..." />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Selling price (£)">
            <input className="input" type="number" step="0.01" min="0" {...field('price')} placeholder="0.00" />
          </Field>
          <Field label="What you paid (£)">
            <input className="input" type="number" step="0.01" min="0" {...field('buyPrice')} placeholder="For profit tracking" />
          </Field>
        </div>

        <Field label="Condition">
          <select className="input" {...field('condition')}>
            {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
      </div>

      {/* ── Item specifics ──────────────────────────────────── */}
      <div className="card p-4 flex flex-col gap-4">
        <div className="text-[13px] font-medium border-b border-white/[0.05] pb-2">Item specifics</div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Brand / Designer"><input className="input" {...field('brand')} placeholder="e.g. Nike" /></Field>
          <Field label="Size"><input className="input" {...field('size')} placeholder="e.g. M, 12, XL, 8UK" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Colour"><input className="input" {...field('colour')} placeholder="e.g. Navy blue" /></Field>
          <Field label="Material"><input className="input" {...field('material')} placeholder="e.g. Cotton, Leather" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category"><input className="input" {...field('category')} placeholder="e.g. Clothing" /></Field>
          <Field label="Sub-category"><input className="input" {...field('subcategory')} placeholder="e.g. T-shirts" /></Field>
        </div>
        <Field label="SKU / Reference"><input className="input" {...field('sku')} placeholder="Your internal SKU" /></Field>

        {/* Tags */}
        <Field label={`Tags / Keywords (${form.tags.length}/20)`}>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {form.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/25 text-[11px] text-primary-300">
                {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-white transition-colors"><X size={10} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
              placeholder="Type a tag and press Enter"
              maxLength={30}
            />
            <button onClick={addTag} disabled={!tagInput.trim()} className="btn-secondary !px-3 flex items-center gap-1">
              <Tag size={12} /> Add
            </button>
          </div>
          <p className="text-[10px] text-white/20 mt-1">Tags improve search visibility. Press Enter or comma to add.</p>
        </Field>
      </div>

      {/* ── Publish to platforms ─────────────────────────────── */}
      {connectedPlatforms.length > 0 && (
        <div className="card p-4">
          <div className="text-[13px] font-medium border-b border-white/[0.05] pb-2 mb-3">Publish to platforms</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {connectedPlatforms.map((p) => {
              const existing = platformConnections.find((pl) => pl.platform === p);
              const isOn = selectedPlatforms.has(p);
              return (
                <button
                  key={p}
                  onClick={() => {
                    const next = new Set(selectedPlatforms);
                    next.has(p) ? next.delete(p) : next.add(p);
                    setSelectedPlatforms(next);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${isOn ? 'border-secondary-400/30 bg-secondary-400/[0.06]' : 'border-white/[0.07] bg-white/[0.02] hover:border-white/20'}`}
                >
                  <div className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center text-white flex-shrink-0" style={{ background: PLATFORM_COLOURS[p] ?? '#555' }}>
                    {(PLATFORM_NAMES[p as Platform] ?? p).slice(0, 2)}
                  </div>
                  <span className={`text-[12px] ${isOn ? 'text-secondary-400' : 'text-white/60'}`}>{PLATFORM_NAMES[p as Platform] ?? p}</span>
                  {existing && (
                    <span className={`text-[9px] px-1 py-0.5 rounded border ${existing.status === 'live' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-white/[0.04] text-white/30 border-white/10'}`}>
                      {existing.status}
                    </span>
                  )}
                  {isOn && <Check size={11} className="text-secondary-400 ml-auto" />}
                </button>
              );
            })}
          </div>
          {selectedPlatforms.size > 0 && [...selectedPlatforms].some((p) => platformConnections.find((pl) => pl.platform === p && pl.status === 'live')) && (
            <p className="text-[10px] text-amber-400/70 mb-2">Platforms marked "live" will have their existing listing updated — not duplicated.</p>
          )}
          <button
            onClick={() => crosslist.mutate(Array.from(selectedPlatforms))}
            disabled={selectedPlatforms.size === 0 || crosslist.isPending}
            className="btn-primary w-full flex items-center justify-center gap-2 !text-[12px]"
          >
            {crosslist.isPending ? <><Loader2 size={13} className="animate-spin" /> Queueing...</> : <><Repeat size={13} /> {selectedPlatforms.size === 0 ? 'Select platforms above' : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? 's' : ''}`}</>}
          </button>
        </div>
      )}

      {/* ── Platform connections / sync ─────────────────────── */}
      {platformConnections.length > 0 && (
        <div className="card p-4">
          <div className="text-[13px] font-medium border-b border-white/[0.05] pb-2 mb-3">Platform connections</div>
          <div className="flex flex-wrap gap-2">
            {platformConnections.map((pl: any) => (
              <div key={pl.platform} className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.07] rounded-lg px-3 py-2">
                <div className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center text-white" style={{ background: PLATFORM_COLOURS[pl.platform] ?? '#555' }}>
                  {(PLATFORM_NAMES[pl.platform as Platform] ?? pl.platform).slice(0, 2)}
                </div>
                <span className="text-[12px] capitalize text-white/70">{pl.platform}</span>
                <span className={`text-[9.5px] px-1.5 py-0.5 rounded border ${
                  pl.status === 'live' ? 'bg-secondary-400/10 text-secondary-400 border-secondary-400/20'
                  : pl.status === 'failed' ? 'bg-danger/10 text-danger border-danger/20'
                  : 'bg-white/[0.04] text-white/30 border-white/10'
                }`}>{pl.status}</span>
                {SYNC_STATUSES.includes(pl.status) && (
                  <button
                    onClick={() => sync.mutate(pl.platform)}
                    disabled={syncingPlatform === pl.platform}
                    className="flex items-center gap-1 text-[11px] text-secondary-400 hover:text-secondary-300 ml-1"
                  >
                    <RefreshCw size={11} className={syncingPlatform === pl.platform ? 'animate-spin' : ''} />
                    {syncingPlatform === pl.platform ? 'Syncing...' : pl.status === 'live' ? 'Sync changes' : 'Retry'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {save.isError && <p className="text-[11.5px] text-danger">Failed to save — please try again.</p>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10.5px] font-medium text-white/35 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}
